import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Check, ClipboardList, Loader2, TriangleAlert } from 'lucide-react';
import { WorksheetFieldInput } from '@/components/worksheets/WorksheetFieldInput';

/**
 * The client's side of a practitioner's intake form.
 *
 * No sign-in, deliberately: most of a practitioner's clients will never be members here, and
 * making them create an account to fill in a health history is how a form goes unfilled. The
 * one-time link in their confirmation email IS the credential, and it only ever opens their own
 * booking's form.
 */
export default function IntakeFormFillPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [missing, setMissing] = useState([]);

  useEffect(() => {
    axios.get(`${API}/intake/${token}`)
      .then((r) => {
        setData(r.data);
        setAnswers(r.data.answers || {});
        setName(r.data.client_name || '');
        setDone(!!r.data.already_submitted);
      })
      .catch((e) => setError(e?.response?.data?.detail || 'This form link is not valid.'));
  }, [token]);

  const submit = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/intake/${token}`, { answers, client_name: name });
      setDone(true);
      setMissing([]);
      toast.success('Sent — thank you');
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Could not send that';
      setMissing(detail.startsWith('Please answer') ? [detail] : []);
      toast.error(detail);
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-6"
           data-testid="intake-fill-error">
        <div className="max-w-md w-full bg-white border rounded-2xl p-8 text-center">
          <TriangleAlert className="w-9 h-9 text-amber-500 mx-auto mb-4" />
          <h1 className="text-base md:text-lg font-semibold mb-2">This form won&apos;t open</h1>
          <p className="text-sm text-gray-500">{error}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="intake-fill-loading">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  const when = data.session?.scheduled_at
    ? new Date(data.session.scheduled_at).toLocaleString()
    : null;

  return (
    <div className="min-h-screen bg-stone-50 py-10 px-5" data-testid="intake-fill-page">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white border rounded-2xl overflow-hidden">
          <div className="px-7 pt-7 pb-5 border-b">
            <ClipboardList className="w-7 h-7 text-cyan-600 mb-3" />
            <h1 className="text-lg md:text-xl font-semibold">{data.form.title}</h1>
            {(data.session?.title || when) && (
              <p className="text-sm text-gray-500 mt-1" data-testid="intake-fill-session">
                {data.session?.title}{data.session?.title && when ? ' · ' : ''}{when}
              </p>
            )}
            {data.form.intro && (
              <p className="text-sm text-gray-600 mt-3 whitespace-pre-wrap">{data.form.intro}</p>
            )}
          </div>

          {done ? (
            <div className="px-7 py-10 text-center" data-testid="intake-fill-done">
              <Check className="w-9 h-9 text-emerald-600 mx-auto mb-3" />
              <p className="text-sm font-medium">Your answers are with your practitioner</p>
              <p className="text-xs text-gray-500 mt-1">
                You can close this page. Come back to this link any time to change an answer.
              </p>
              <Button variant="outline" className="mt-5" onClick={() => setDone(false)}
                      data-testid="intake-fill-edit-again">
                Change something
              </Button>
            </div>
          ) : (
            <div className="px-7 py-6 space-y-6">
              <div>
                <label className="text-sm text-gray-700 block mb-1" htmlFor="intake-name">
                  Your name
                </label>
                <Input id="intake-name" value={name} onChange={(e) => setName(e.target.value)}
                       placeholder="First and last name" data-testid="intake-fill-name" />
              </div>

              {(data.form.fields || []).map((field) => (
                <WorksheetFieldInput
                  key={field.id}
                  field={field}
                  value={answers[field.id]}
                  onChange={(v) => setAnswers({ ...answers, [field.id]: v })}
                />
              ))}

              {missing.length > 0 && (
                <p className="text-sm text-rose-600" data-testid="intake-fill-missing">
                  {missing[0]}
                </p>
              )}

              <Button onClick={submit} disabled={busy} className="w-full"
                      data-testid="intake-fill-submit">
                {busy ? 'Sending…' : 'Send to my practitioner'}
              </Button>
              <p className="text-xs text-gray-400 text-center">
                Only your practitioner sees these answers.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
